import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    Building2,
    Sparkles,
    AlertOctagon,
    Package,
    Users2,
    BriefcaseBusiness,
    CalendarRange,
    Plug,
    Shirt,
    CalendarClock,
    BookOpen,
    Repeat2,
    ShieldCheck,
    KeyRound,
    LogOut,
    Search,
    Bell,
    ChevronDown,
    MapPinned,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ZentraLogo from "@/components/ZentraLogo";
import { ROLE_LABELS } from "@/lib/dictionaries";
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";

const NAV_ITEMS = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/propiedades", label: "Propiedades", icon: Building2 },
    { to: "/mapa", label: "Mapa", icon: MapPinned },
    { to: "/calendario", label: "Calendario", icon: CalendarRange },
    { to: "/limpiezas", label: "Limpiezas", icon: Sparkles },
    { to: "/lavanderia", label: "Lavandería", icon: Shirt },
    { to: "/incidencias", label: "Incidencias", icon: AlertOctagon },
    { to: "/almacen", label: "Almacén", icon: Package },
    { to: "/personal", label: "Personal", icon: Users2 },
    { to: "/horarios", label: "Horarios", icon: CalendarClock },
    { to: "/rutinas", label: "Rutinas", icon: Repeat2 },
    { to: "/contabilidad", label: "Contabilidad", icon: BookOpen, financialOnly: true },
    { to: "/clientes", label: "Clientes", icon: BriefcaseBusiness },
    { to: "/usuarios", label: "Usuarios", icon: ShieldCheck, adminOnly: true },
    { to: "/integraciones", label: "Integraciones", icon: Plug, adminOnly: true },
];

// Routes hidden from specific roles (in addition to adminOnly / financialOnly tags).
const ROLE_FORBIDDEN_ROUTES = {
    warehouse: ["/usuarios", "/integraciones", "/contabilidad"],
};

function getNavForRole(role) {
    const forbidden = new Set(ROLE_FORBIDDEN_ROUTES[role] || []);
    return NAV_ITEMS.filter((item) => {
        if (item.adminOnly && !["super_admin", "admin"].includes(role)) return false;
        if (item.financialOnly && !["super_admin", "admin", "coordinator"].includes(role)) return false;
        if (forbidden.has(item.to)) return false;
        return true;
    });
}

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const handleChangePassword = async () => {
        setMenuOpen(false);
        const current = window.prompt("Contraseña actual:");
        if (!current) return;
        const next = window.prompt("Nueva contraseña (mín. 6 caracteres):");
        if (!next) return;
        if (next.length < 6) {
            alert("La nueva contraseña debe tener al menos 6 caracteres");
            return;
        }
        const confirm = window.prompt("Repite la nueva contraseña:");
        if (confirm !== next) {
            alert("Las contraseñas no coinciden");
            return;
        }
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            alert("Contraseña actualizada correctamente.");
        } catch (err) {
            alert(formatApiError(err));
        }
    };

    const navItems = getNavForRole(user?.role);

    const currentTitle =
        NAV_ITEMS.find((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)))?.label ||
        "ZENTRA";

    return (
        <div className="flex min-h-screen bg-zentra-bg">
            {/* Sidebar */}
            <aside
                className="hidden lg:flex w-64 flex-col text-zentra-bg sticky top-0 h-screen"
                style={{ background: "#18392B" }}
                data-testid="sidebar"
            >
                <div className="px-6 pt-7 pb-8 border-b border-white/5">
                    <ZentraLogo variant="light" layout="horizontal" size="md" />
                </div>
                <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
                    <p className="px-3 text-[10px] font-semibold tracking-widest text-white/40 uppercase mb-2">
                        Operaciones
                    </p>
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            className={({ isActive }) => `zentra-nav-link ${isActive ? "is-active" : ""}`}
                            data-testid={`nav-${item.label.toLowerCase()}`}
                        >
                            <item.icon size={17} strokeWidth={1.7} />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="px-4 py-4 border-t border-white/5">
                    <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">Centro operativo</div>
                    <p className="text-xs text-white/60 leading-relaxed">
                        El centro de operaciones de tu negocio.
                    </p>
                </div>
            </aside>

            {/* Main column */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Topbar */}
                <header className="bg-white border-b border-zentra-border sticky top-0 z-30" data-testid="topbar">
                    <div className="flex items-center justify-between px-6 py-3 gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="lg:hidden">
                                <ZentraLogo variant="dark" layout="horizontal" size="sm" />
                            </div>
                            <div className="hidden lg:flex flex-col leading-tight">
                                <span className="text-[11px] tracking-widest uppercase text-zentra-muted">
                                    Módulo
                                </span>
                                <h1 className="font-display text-lg font-semibold text-zentra-ink">
                                    {currentTitle}
                                </h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-zentra-bg border border-zentra-border rounded-lg text-zentra-muted text-sm">
                                <Search size={14} />
                                <input
                                    className="bg-transparent border-0 outline-none text-sm w-44 placeholder:text-zentra-muted text-zentra-ink"
                                    placeholder="Buscar…"
                                    data-testid="topbar-search"
                                />
                            </div>
                            <button
                                type="button"
                                className="relative w-9 h-9 rounded-lg border border-zentra-border bg-white flex items-center justify-center hover:border-zentra-olive transition"
                                data-testid="topbar-notif"
                            >
                                <Bell size={16} className="text-zentra-ink" />
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-zentra-accent" />
                            </button>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setMenuOpen((v) => !v)}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zentra-border bg-white hover:border-zentra-olive transition"
                                    data-testid="topbar-user"
                                >
                                    <div className="w-7 h-7 rounded-md bg-zentra-primary text-white font-display font-semibold flex items-center justify-center text-xs">
                                        {(user?.name || "U").slice(0, 1).toUpperCase()}
                                    </div>
                                    <div className="hidden md:flex flex-col leading-tight items-start">
                                        <span className="text-xs font-semibold text-zentra-ink">{user?.name}</span>
                                        <span className="text-[10px] text-zentra-muted tracking-wider uppercase">
                                            {ROLE_LABELS[user?.role] || user?.role}
                                        </span>
                                    </div>
                                    <ChevronDown size={14} className="text-zentra-muted" />
                                </button>
                                {menuOpen && (
                                    <div
                                        className="absolute right-0 top-12 w-56 bg-white border border-zentra-border rounded-lg shadow-lg p-2 z-50"
                                        data-testid="topbar-user-menu"
                                    >
                                        <div className="px-3 py-2 border-b border-zentra-border mb-1">
                                            <p className="text-sm font-semibold text-zentra-ink">{user?.name}</p>
                                            <p className="text-xs text-zentra-muted">{user?.email}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleChangePassword}
                                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-zentra-ink hover:bg-zentra-bg transition"
                                            data-testid="change-password-button"
                                        >
                                            <KeyRound size={14} />
                                            Cambiar contraseña
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleLogout}
                                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-zentra-ink hover:bg-zentra-bg transition"
                                            data-testid="logout-button"
                                        >
                                            <LogOut size={14} />
                                            Cerrar sesión
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Mobile nav */}
                    <nav
                        className="lg:hidden flex items-center gap-1 px-2 pb-2 overflow-x-auto"
                        data-testid="mobile-nav"
                    >
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition ${
                                        isActive
                                            ? "bg-zentra-primary text-white"
                                            : "text-zentra-ink bg-zentra-bg hover:bg-zentra-border/50"
                                    }`
                                }
                            >
                                <item.icon size={14} />
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                </header>

                <main className="flex-1 p-5 md:p-8 max-w-[1500px] w-full mx-auto" data-testid="main-content">
                    {children}
                </main>
            </div>
        </div>
    );
}
